import { Country, ProjectInvoiceTo } from '@consigli/types';

import { commonValidators, ValidationRules } from '@/utils/validation';

import { CreateProjectFormData } from '../types';

/**
 * Validation config for the project form
 */
export const validationRules: ValidationRules<CreateProjectFormData> = {
  name: {
    required: true,
    minLength: 2,
    validate: (value: unknown, ...args: unknown[]): string | undefined => {
      const existingProjectNames = args[0] as string[] | undefined;
      const lengthError = commonValidators.stringWithMinLength(
        value,
        2,
        'Project name must be at least 2 characters',
      );
      if (lengthError) return lengthError;

      if (value && typeof value === 'string' && existingProjectNames?.includes(value)) {
        return 'A project with this name already exists';
      }

      return undefined;
    },
  },
  address: {
    required: false,
    validate: (value: unknown): string | undefined => {
      return commonValidators.optionalString(value);
    },
  },
  country: {
    required: true,
    validate: (value: unknown): string | undefined => {
      const validCountries = Object.values(Country);
      return commonValidators.enumValue(value, validCountries, 'Country is required');
    },
  },
  specifications: {
    required: false,
    validate: (value: unknown): string | undefined => {
      return commonValidators.optionalArray(value);
    },
  },
  constructionTypes: {
    required: true,
    minItems: 1,
    validate: (value: unknown): string | undefined => {
      return commonValidators.requiredArray(value, 'At least one construction type is required');
    },
  },
  organizationId: {
    required: true,
    validate: (value: unknown): string | undefined => {
      return commonValidators.requiredString(value, 'Organization is required');
    },
  },
  billingInformation: {
    required: false,
    validate: (value: unknown): string | undefined => {
      return commonValidators.optionalString(value);
    },
  },
  billingAddress: {
    required: false, // Will be conditionally required based on invoiceTo
    minLength: 1,
    validate: (value: unknown, ...args: unknown[]): string | undefined => {
      const invoiceTo = args[0] as ProjectInvoiceTo;
      if (invoiceTo === ProjectInvoiceTo.PROJECT) {
        return commonValidators.stringWithMinLength(
          value,
          1,
          'Billing address is required when invoicing to project',
        );
      }
      return commonValidators.optionalString(value);
    },
  },
  orgNumber: {
    required: false, // Will be conditionally required based on invoiceTo
    minValue: 0,
    validate: (value: unknown, ...args: unknown[]): string | undefined => {
      const invoiceTo = args[0] as ProjectInvoiceTo;

      if (invoiceTo === ProjectInvoiceTo.PROJECT) {
        // Required when invoicing to project
        return commonValidators.positiveInteger(
          value,
          'Organization number is required when invoicing to project',
        );
      } else {
        // Optional but validate format if provided
        if (value !== null && value !== undefined) {
          return commonValidators.positiveInteger(
            value,
            'Organization number must be a valid positive number',
          );
        }
      }

      return undefined;
    },
  },
  invoiceTo: {
    required: true,
    validate: (value: unknown): string | undefined => {
      const validInvoiceTo = Object.values(ProjectInvoiceTo);
      return commonValidators.enumValue(value, validInvoiceTo, 'Invoice to is required');
    },
  },
  oracleProjectNumber: {
    required: false,
    validate: (): string | undefined => undefined,
  },
  availableServicesOverride: {
    required: false,
    validate: (): string | undefined => {
      return undefined;
    },
  },
} as const;
